import { useRef, useEffect } from 'react';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
//  import am4themes_dark from "@amcharts/amcharts4/themes/dark";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4lang_es_ES from "@amcharts/amcharts4/lang/es_ES";

export default function Charts({ type, idContent, classContent, data, dataSeries, settings, colorList, initialDate = null, endDate = null }) {
    am4core.useTheme(am4themes_animated);
    am4core.addLicense("CH234196226");
    am4core.useTheme(am4themes_crosland);

    switch (type) {
        case "LinealMultiAxis":
            return LinealMultiAxis(idContent, data, dataSeries, settings, colorList, classContent);
        case "StackedColumn100":
            return StackedColumn100(idContent, data, dataSeries, settings, colorList, classContent);
        case "StackedArea100":
            return StackedArea100(idContent, data, dataSeries, settings, colorList, classContent);
        case "CandlestickChart":
            return CandlestickChart(idContent, data, dataSeries, settings, colorList, classContent);
        case "AreaChart":
            return AreaChart(idContent, data, dataSeries, settings, colorList, classContent, initialDate, endDate);
        default:
            return (<></>)
    }
}

function am4themes_crosland(target) {
    if (target instanceof am4core.ColorSet) {
        target.list = [
            am4core.color("#3699FF"),
            am4core.color("#F64E60")
        ];
    }
}

function LinealMultiAxis(idContent, data, dataSeries, settings, colorList, classContent) {
    const chartRef = useRef(null);

    useEffect(() => {
        var chart = am4core.create(idContent, am4charts.XYChart);
        //chart.language.locale = am4lang_es_ES;
        //chart.dateFormatter.language = new am4core.Language();
        chart.dateFormatter.language.locale = am4lang_es_ES;
        if (settings.inputDateFormat) chart.dateFormatter.inputDateFormat = settings.inputDateFormat;

        // Add data
        chart.data = data;

        var dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.renderer.minGridDistance = 100;
        dateAxis.renderer.labels.template.disabled = settings.disableLabelX;
        dateAxis.tooltip.disabled = settings.disableTooltipX;
        dateAxis.tooltip.dy = settings.tooltipDy;
        dateAxis.tooltip.background.fill = am4core.color('#000');
        dateAxis.tooltip.label.fill = am4core.color('#fff');
        dateAxis.groupData = true;
        dateAxis.renderer.grid.template.location = 0;
        dateAxis.renderer.labels.template.location = 0.45;
        let periodChange = "yyyy";
        if (settings.periodChange) periodChange = settings.periodChange;
        dateAxis.periodChangeDateFormats.setKey("month", `[bold]${periodChange}`);

        var valueAxisIzq = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxisIzq.renderer.labels.template.disabled = settings.disableLabelY;
        valueAxisIzq.tooltip.disabled = settings.disableTooltipY;
        valueAxisIzq.tooltip.background.fill = am4core.color('#000');
        valueAxisIzq.tooltip.label.fill = am4core.color('#fff');
        settings.minIzq && (valueAxisIzq.min = settings.minIzq);
        settings.maxIzq && (valueAxisIzq.max = settings.maxIzq);
        valueAxisIzq.title.text = settings.titleYizq;
        valueAxisIzq.title.rotation = 270;
        valueAxisIzq.title.align = "center";
        valueAxisIzq.title.valign = "middle";
        valueAxisIzq.renderer.labels.template.adapter.add("text", function (text) {
            return text + settings.labelAdapterAxisYIzq;
        });
        if (settings.valueMaxIzq != null) valueAxisIzq.max = settings.valueMaxIzq;
        if (settings.valueMinIzq != null) valueAxisIzq.min = settings.valueMinIzq;

        var valueAxisDer = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxisDer.renderer.labels.template.disabled = settings.disableLabelY;
        valueAxisDer.tooltip.disabled = settings.disableTooltipY;
        valueAxisDer.tooltip.background.fill = am4core.color('#000');
        valueAxisDer.tooltip.label.fill = am4core.color('#fff');
        settings.minDer && (valueAxisDer.min = settings.minDer);
        settings.maxDer && (valueAxisDer.max = settings.maxDer);
        valueAxisDer.title.text = settings.titleYder;
        valueAxisDer.title.rotation = 270;
        valueAxisDer.title.align = "center";
        valueAxisDer.title.valign = "middle";
        valueAxisDer.renderer.opposite = true;
        valueAxisDer.renderer.labels.template.adapter.add("text", function (text) {
            return text + settings.labelAdapterAxisYDer;
        });
        if (settings.valueMaxDer != null) valueAxisDer.max = settings.valueMaxDer;
        if (settings.valueMinDer != null) valueAxisDer.min = settings.valueMinDer;

        valueAxisIzq.syncWithAxis = valueAxisDer;

        function createSeries(name, field, typeSerie, color1, color2, axis, tooltipPrev, tooltipAft, strokeDasharray) {
            let series;
            let gradient = new am4core.LinearGradient();
            gradient.addColor(color1, 0.8);
            gradient.addColor(color2, 0.8);

            if (typeSerie === 'column') {
                series = chart.series.push(new am4charts.ColumnSeries());
                series.fillOpacity = 1;
                series.strokeWidth = 0.6;
                series.columns.template.width = am4core.percent(settings.widthColumn);
                series.fill = gradient;
                series.stroke = gradient;
            }
            else if (typeSerie === 'line') {
                series = chart.series.push(new am4charts.LineSeries());
                series.fillOpacity = 0;
                series.strokeWidth = settings.strokeWidth ?? 5;
                //smoother lines
                series.tensionX = 0.9;
                series.tensionY = 0.9;
                series.fill = gradient;
                series.stroke = gradient;
            }
            series.name = name;
            series.dataFields.valueY = field;
            series.strokeDasharray = strokeDasharray;
            if (axis === "left") series.yAxis = valueAxisIzq;
            else if (axis === "right") series.yAxis = valueAxisDer;
            series.dataFields.dateX = "date";
            series.tooltipText = `{name}: ${tooltipPrev} {valueY.value} ${tooltipAft}` + settings.tooltipAft;

            let shadow = series.filters.push(new am4core.DropShadowFilter());
            shadow.color = am4core.color(color1);
            shadow.dx = 5;
            shadow.dy = 5;
            shadow.blur = 8;
            shadow.opacity = 0.5;

            // Add simple bullet
            if (settings.enableBullet && typeSerie === 'line') {
                let circleBullet = series.bullets.push(new am4charts.CircleBullet());
                circleBullet.stroke = am4core.color("#fff");
                circleBullet.strokeWidth = 1;
                circleBullet.fill = am4core.color(color1);
                circleBullet.fillOpacity = 1;
                circleBullet.circle.radius = settings.circleBulletRadius ?? 3;
            }
        }

        dataSeries.forEach(function (element, index, array) {
            createSeries(element.name, element.field, element.typeSerie, am4core.color(colorList[2 * index]),
                am4core.color(colorList[2 * index + 1]), element.axis, element.tooltipPrev, element.tooltipAft, element.strokeDasharray);
        });

        //labels
        settings.labels?.forEach(labelText => {
            var label = chart.createChild(am4core.Label);
            label.html = `<i>${labelText}</i>`;
            label.fontSize = 13;
            label.x = -14;
        });

        chart.cursor = new am4charts.XYCursor();
        // Enable export
        chart.exporting.menu = new am4core.ExportMenu();
        // Add a legend
        if (settings.enableLegend) {
            chart.legend = new am4charts.Legend();
            chart.legend.dy = 10;
            chart.legend.dx = 60;
            chart.legend.valign = "middle";
        }

        chartRef.current = chart;

        return () => {
            chart.dispose();
        };
    }, [data]);

    return (
        <div id={idContent} className={classContent}></div>
    )
}

function StackedColumn100(idContent, data, dataSeries, settings, colorList, classContent) {
    const chartRef = useRef(null);

    useEffect(() => {
        let chart = am4core.create(idContent, am4charts.XYChart);
        chart.hiddenState.properties.opacity = 0; // this creates initial fade-in
        //chart.language.locale = am4lang_es_ES;

        // Add data
        chart.data = data;

        chart.numberFormatter.numberFormat = "#.0a";
        chart.numberFormatter.precision = "2";
        chart.numberFormatter.bigNumberPrefixes = [
            { "number": 1e+3, "suffix": "K" },
            { "number": 1e+6, "suffix": "M" },
            { "number": 1e+9, "suffix": "B" }
        ];

        chart.colors.step = 2;
        chart.padding(30, 30, 10, 30);

        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = "category";
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.minGridDistance = 100;
        categoryAxis.renderer.labels.template.disabled = settings.disableLabelX;
        categoryAxis.tooltip.disabled = settings.disableTooltipX;
        categoryAxis.tooltip.dy = settings.tooltipDy;
        categoryAxis.tooltip.background.fill = am4core.color('#000');
        categoryAxis.tooltip.label.fill = am4core.color('#fff');
        categoryAxis.title.text = settings.titleX;
        categoryAxis.title.align = "center";
        categoryAxis.title.valign = "middle";
        categoryAxis.renderer.cellStartLocation = 0.4;
        categoryAxis.renderer.cellEndLocation = 0.6;

        //categoryAxis.groupData = true;

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        if (settings.isStacked) {
            valueAxis.min = 0;
            valueAxis.max = 100;
            valueAxis.strictMinMax = true;
            valueAxis.calculateTotals = settings.isStacked;
            valueAxis.renderer.minWidth = 50;
        }


        valueAxis.renderer.labels.template.disabled = settings.disableLabelY;
        valueAxis.tooltip.disabled = settings.disableTooltipY;
        valueAxis.tooltip.background.fill = am4core.color('#000');
        valueAxis.tooltip.label.fill = am4core.color('#fff');

        valueAxis.title.text = settings.titleY;
        valueAxis.title.rotation = 270;
        valueAxis.title.align = "center";
        valueAxis.title.valign = "middle";
        valueAxis.renderer.labels.template.adapter.add("text", function (text) {
            return text + settings.labelAdapterAxisY;
        });

        function createSeries(name, field, color1, tooltipPrev, tooltipAft) {
            let series = chart.series.push(new am4charts.ColumnSeries());

            series.columns.template.width = am4core.percent(settings.widthColumn);
            // series.columns.template.tooltipText =
            //     "{name}: {valueY.value.totalPercent.formatNumber('#.00')}%";
            series.name = name;
            series.dataFields.categoryX = "category";
            series.dataFields.valueY = field;
            //if (settings.isStacked) series.dataFields.valueYShow = "totalPercent";
            series.dataItems.template.locations.categoryX = 0.5;
            series.stacked = settings.isStacked;
            series.tooltip.pointerOrientation = "vertical";

            let gradient = new am4core.LinearGradient();
            gradient.addColor(color1, 0.8);
            gradient.addColor(color1, 0.8);

            series.fillOpacity = 1;
            series.strokeWidth = 0.6;
            series.fill = gradient;
            series.stroke = gradient;
            series.tooltip.getFillFromObject = false;
            series.tooltip.background.fill = am4core.color("#000");
            series.tooltip.label.fill = am4core.color("#fff");

            // Set up tooltip
            series.adapter.add("tooltipText", function (ev) {
                var text = "[bold]{category}[/]\n"
                chart.series.each(function (item, index, array) {
                    if (!item.isHidden) {
                        text += "[" + colorList[index] + "]●[/] " + item.name + ": {" + item.dataFields.valueY + "} " + settings.tooltipAft + "\n";
                    }
                });
                return text;
            });

            // series.tooltipText = `{name}: ${tooltipPrev ? tooltipPrev : ""} ${`{valueY.value.formatNumber('#.00')}`} ${tooltipAft ? tooltipAft : ""}` + settings.tooltipAft;

            let shadow = series.filters.push(new am4core.DropShadowFilter());
            shadow.color = am4core.color(color1);
            shadow.dx = 2;
            shadow.dy = 2;
            shadow.blur = 5;
            shadow.opacity = 0.5;

            // Add simple bullet
            if (settings.enableBullet) {

                let bullet = series.bullets.push(new am4charts.LabelBullet());

                bullet.interactionsEnabled = false;
                bullet.label.text = "{valueY.totalPercent.formatNumber('#.00')}%";
                bullet.label.fill = am4core.color("#ffffff");
                bullet.locationY = 0.5;
            }
        }

        dataSeries.forEach(function (element, index, array) {
            createSeries(element.name, element.field, am4core.color(colorList[index]), element.tooltipPrev, element.tooltipAft);
        });


        chart.cursor = new am4charts.XYCursor();
        chart.cursor.maxTooltipDistance = -1;
        // Enable export
        chart.exporting.menu = new am4core.ExportMenu();
        // Add a legend
        if (settings.enableLegend) {
            chart.legend = new am4charts.Legend();
            chart.legend.dy = 10;
            chart.legend.maxWidth = 100;
            chart.legend.dx = 60;
            chart.legend.valign = "middle";
        }

        chartRef.current = chart;

        return () => {
            chart.dispose();
        };
    }, [data, settings]);

    return (
        <div id={idContent} className={classContent}></div>
    )
}

function StackedArea100(idContent, data, dataSeries, settings, colorList, classContent) {
    const chartRef = useRef(null);

    useEffect(() => {
        let chart = am4core.create(idContent, am4charts.XYChart);
        chart.hiddenState.properties.opacity = 0; // this creates initial fade-in
        //chart.language.locale = am4lang_es_ES;

        // Add data
        chart.data = data;

        chart.colors.step = 2;
        chart.padding(30, 30, 10, 30);

        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = "category";
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.minGridDistance = 100;
        categoryAxis.renderer.labels.template.disabled = settings.disableLabelX;
        categoryAxis.tooltip.disabled = settings.disableTooltipX;
        categoryAxis.tooltip.dy = settings.tooltipDy;
        categoryAxis.tooltip.background.fill = am4core.color('#000');
        categoryAxis.tooltip.label.fill = am4core.color('#fff');
        categoryAxis.title.text = settings.titleX;
        categoryAxis.title.align = "center";
        categoryAxis.title.valign = "middle";

        //categoryAxis.groupData = true;

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        if (settings.isStacked) {
            valueAxis.min = 0;
            valueAxis.max = 100;
            valueAxis.strictMinMax = true;
            valueAxis.calculateTotals = settings.isStacked;
            valueAxis.renderer.minWidth = 50;
        }


        valueAxis.renderer.labels.template.disabled = settings.disableLabelY;
        valueAxis.tooltip.disabled = settings.disableTooltipY;
        valueAxis.tooltip.background.fill = am4core.color('#000');
        valueAxis.tooltip.label.fill = am4core.color('#fff');

        valueAxis.title.text = settings.titleY;
        valueAxis.title.rotation = 270;
        valueAxis.title.align = "center";
        valueAxis.title.valign = "middle";
        valueAxis.renderer.labels.template.adapter.add("text", function (text) {
            return text + settings.labelAdapterAxisY;
        });

        function createSeries(name, field, color1, tooltipPrev, tooltipAft) {
            let series = chart.series.push(new am4charts.LineSeries());

            series.name = name;
            series.dataFields.categoryX = "category";
            series.dataFields.valueY = field;
            //if (settings.isStacked) series.dataFields.valueYShow = "totalPercent";
            series.dataItems.template.locations.categoryX = 0.5;
            series.stacked = settings.isStacked;
            series.tooltip.pointerOrientation = "vertical";

            let gradient = new am4core.LinearGradient();
            gradient.addColor(color1, 0.8);
            gradient.addColor(color1, 0.8);

            series.fillOpacity = 1;
            series.strokeWidth = 0.6;
            series.fill = gradient;
            series.stroke = gradient;

            // series.fillOpacity = 0.6;
            // series.strokeWidth = 2;

            //series.tooltipText = `{name}: ${tooltipPrev ? tooltipPrev : ""} ${settings.isStacked && `{valueY.value.formatNumber('#.00')}`} ${tooltipAft ? tooltipAft : ""}` + settings.tooltipAft;

            // Set up tooltip
            series.adapter.add("tooltipText", function (ev) {
                let text = "[bold]{dateX}[/]\n";
                chart.series.each(function (item, index, array) {
                    if (!item.isHidden) {
                        text += "[" + colorList[index] + "]●[/] " + item.name + ": " + (tooltipPrev ? tooltipPrev : "") + "{" + item.dataFields.valueY + "}" + (tooltipAft ? tooltipAft : "") + "\n";
                    }
                });
                return text;
            });

            series.tooltip.getFillFromObject = false;
            series.tooltip.background.fill = am4core.color("#fff");
            series.tooltip.label.fill = am4core.color("#00");


            let shadow = series.filters.push(new am4core.DropShadowFilter());
            shadow.color = am4core.color(color1);
            shadow.dx = 5;
            shadow.dy = 5;
            shadow.blur = 8;
            shadow.opacity = 0.5;

            // Add simple bullet
            if (settings.enableBullet) {

                let bullet = series.bullets.push(new am4charts.LabelBullet());

                bullet.interactionsEnabled = false;
                bullet.label.text = "{valueY.totalPercent.formatNumber('#.00')}%";
                bullet.label.fill = am4core.color("#ffffff");
                bullet.locationY = 0.5;
            }
        }

        dataSeries.forEach(function (element, index, array) {
            createSeries(element.name, element.field, am4core.color(colorList[index]), element.tooltipPrev, element.tooltipAft);
        });


        chart.cursor = new am4charts.XYCursor();
        chart.cursor.maxTooltipDistance = -1;
        // Enable export
        chart.exporting.menu = new am4core.ExportMenu();
        // Add a legend
        if (settings.enableLegend) {
            chart.legend = new am4charts.Legend();
            chart.legend.dy = 10;
            chart.legend.dx = 60;
            chart.legend.valign = "middle";
        }

        chartRef.current = chart;

        return () => {
            chart.dispose();
        };
    }, [data, settings]);

    return (
        <div id={idContent} className={classContent}></div>
    )
}

function CandlestickChart(idContent, data, dataSeries, settings, colorList, classContent) {
    const chartRef = useRef(null);

    useEffect(() => {
        let chart = am4core.create(idContent, am4charts.XYChart);
        //chart.language.locale = am4lang_es_ES;
        //chart.dateFormatter.language = new am4core.Language();
        //chart.dateFormatter.language.locale = am4lang_es_ES;

        //Add data
        chart.data = data;

        chart.dateFormatter.inputDateFormat = "yyyy-MM-dd";

        let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.renderer.grid.template.location = 0;
        dateAxis.tooltip.disabled = settings.disableTooltipX;
        dateAxis.title.text = settings.titleX;
        dateAxis.renderer.labels.template.disabled = settings.disableLabelX;

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.tooltip.disabled = settings.disableTooltipY;
        valueAxis.title.text = settings.titleY;
        valueAxis.renderer.labels.template.disabled = settings.disableLabelY;

        valueAxis.renderer.labels.template.adapter.add("text", function (text) {
            return text + settings.labelAdapterAxisY;
        });

        let series = chart.series.push(new am4charts.CandlestickSeries());
        series.dataFields.dateX = "date";
        series.dataFields.valueY = settings.close;
        series.dataFields.openValueY = settings.open;
        series.dataFields.lowValueY = settings.low;
        series.dataFields.highValueY = settings.high;
        series.simplifiedProcessing = true;

        series.dropFromOpenState.properties.fill = am4core.color(colorList[0]);
        series.dropFromOpenState.properties.stroke = am4core.color(colorList[0]);

        series.riseFromOpenState.properties.fill = am4core.color(colorList[1]);
        series.riseFromOpenState.properties.stroke = am4core.color(colorList[1]);

        if (settings.noHighLow) {
            series.tooltipText = `Open:${settings.tooltipBfr} {openValueY.value} ${settings.tooltipAft}\nClose:${settings.tooltipBfr} {valueY.value} ${settings.tooltipAft}`;
        } else {
            series.tooltipText = `Open:${settings.tooltipBfr} {openValueY.value} ${settings.tooltipAft}\n
                                  High:${settings.tooltipBfr} {highValueY.value} ${settings.tooltipAft}\n
                                  Low:${settings.tooltipBfr} {lowValueY.value} ${settings.tooltipAft}
                                  Close:${settings.tooltipBfr} {valueY.value} ${settings.tooltipAft}`;
        }
        series.tooltip.autoTextColor = false;



        chart.cursor = new am4charts.XYCursor();

        // a separate series for scrollbar
        let lineSeries = chart.series.push(new am4charts.LineSeries());
        lineSeries.dataFields.dateX = "date";
        lineSeries.dataFields.valueY = "close";
        // need to set on default state, as initially series is "show"
        lineSeries.defaultState.properties.visible = false;

        // hide from legend too (in case there is one)
        lineSeries.hiddenInLegend = true;
        lineSeries.fillOpacity = 0.3;
        lineSeries.strokeOpacity = 0.3;


        //labels
        settings.labels?.forEach(labelText => {
            var label = chart.createChild(am4core.Label);
            label.html = `<i>${labelText}</i>`;
            label.fontSize = 13;
            label.x = -14;
        });




        chart.cursor = new am4charts.XYCursor();
        // Enable export
        chart.exporting.menu = new am4core.ExportMenu();
        // Add a legend
        if (settings.enableLegend) {
            chart.legend = new am4charts.Legend();
            chart.legend.dy = 10;
            chart.legend.dx = 60;

            chart.legend.valign = "middle";
        }

        chartRef.current = chart;
        return () => {
            chart.dispose();
        };
    }, [data]);

    return (
        <div id={idContent} className={classContent}></div>
    )
}

function AreaChart(idContent, data, dataSeries, settings, colorList, classContent, initialDate, endDate) {
    const chartRef = useRef(null);

    useEffect(() => {
        let chart = am4core.create(idContent, am4charts.XYChart);
        chart.hiddenState.properties.opacity = 0; // this creates initial fade-in
        chart.language.locale = am4lang_es_ES;
        chart.dateFormatter.inputDateFormat = "dd/MM/yyyy";
        chart.dateFormatter.dateFormat = "dd-MMM yyyy";

        // Add data
        chart.data = data;

        chart.colors.step = 2;
        chart.padding(30, 30, 10, 30);

        var dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.renderer.minGridDistance = 100;
        dateAxis.renderer.labels.template.disabled = settings.disableLabelX;
        dateAxis.tooltip.disabled = settings.disableTooltipX;
        dateAxis.tooltip.dy = settings.tooltipDy;
        dateAxis.tooltip.background.fill = am4core.color('#000');
        dateAxis.tooltip.label.fill = am4core.color('#fff');
        dateAxis.groupData = true;
        dateAxis.renderer.grid.template.location = 0;
        dateAxis.renderer.labels.template.location = 0.45;
        dateAxis.periodChangeDateFormats.setKey("month", "[bold]yyyy");
        dateAxis.dateFormatter = new am4core.DateFormatter();
        dateAxis.dateFormatter.dateFormat = "dd-MMM-yyyy";

        //categoryAxis.groupData = true;

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());

        valueAxis.renderer.labels.template.disabled = settings.disableLabelY;
        valueAxis.tooltip.disabled = settings.disableTooltipY;
        valueAxis.tooltip.background.fill = am4core.color('#000');
        valueAxis.tooltip.label.fill = am4core.color('#fff');

        valueAxis.title.text = settings.titleY;
        valueAxis.title.rotation = 270;
        valueAxis.title.align = "center";
        valueAxis.title.valign = "middle";
        valueAxis.renderer.labels.template.adapter.add("text", function (text) {
            return text + settings.labelAdapterAxisY;
        });

        function createSeries(name, field, color1, tooltipPrev, tooltipAft) {
            let series = chart.series.push(new am4charts.LineSeries());

            series.name = name;
            series.dataFields.eventName = "eventoName";
            series.dataFields.valueY = field;
            series.dataFields.dateX = "date";
            let htmlToltip = ``;
            htmlToltip +=
                `<card>
                     <span class='fw-bold'>{dateX}: </span> <span>${tooltipPrev} {valueY.value} ${tooltipAft} ${settings.tooltipAft}</span>
                     <div><span class='fw-bold'>Evento: </span> {eventName}</div>
                </card >`;

            series.tooltipHTML = htmlToltip;



            series.tooltip.pointerOrientation = "vertical";

            let gradient = new am4core.LinearGradient();
            gradient.addColor(color1, 0.8);
            gradient.addColor(color1, 0.8);

            series.fillOpacity = 0.6;
            series.strokeWidth = 0.6;
            series.fill = gradient;
            series.stroke = gradient;

            series.tooltip.getFillFromObject = false;
            series.tooltip.background.fill = am4core.color("#fff");
            series.tooltip.label.fill = am4core.color("#00");


            let shadow = series.filters.push(new am4core.DropShadowFilter());
            shadow.color = am4core.color(color1);
            shadow.dx = 5;
            shadow.dy = 5;
            shadow.blur = 8;
            shadow.opacity = 0.5;

            // Add simple bullet
            if (settings.enableBullet) {
                let circleBullet = series.bullets.push(new am4charts.CircleBullet());
                circleBullet.stroke = am4core.color("#fff");
                circleBullet.strokeWidth = 1;
                circleBullet.fill = am4core.color(color1);
                circleBullet.fillOpacity = 1;
                circleBullet.circle.radius = settings.circleBulletRadius ?? 3;
            }
        }

        dataSeries.forEach(function (element, index, array) {
            createSeries(element.name, element.field, am4core.color(colorList[index]), element.tooltipPrev, element.tooltipAft);
        });


        chart.cursor = new am4charts.XYCursor();
        chart.cursor.maxTooltipDistance = -1;
        // Enable export
        chart.exporting.menu = new am4core.ExportMenu();
        // Add a legend
        if (settings.enableLegend) {
            chart.legend = new am4charts.Legend();
            chart.legend.dy = 10;
            chart.legend.dx = 60;
            chart.legend.valign = "middle";
        }

        chart.scrollbarX = new am4core.Scrollbar();
        chart.events.on("ready", function () {
            dateAxis.zoomToDates(
              initialDate,
              endDate
            );
          });

        chartRef.current = chart;

        return () => {
            chart.dispose();
        };
    }, [data, settings]);

    return (
        <div id={idContent} className={classContent}></div>
    )
}

